import { Gender, TobaccoUse } from '@hc-frontend/core-types';
import { removeEmptyKeys } from '@hc-frontend/core-utils-object';
import type { v1 } from '@hc-frontend/services-lead-storage';

import type { Applicant, Child, Spouse } from '../../domain';
import { MemberType } from '../../domain';
import type { userAttributesMapper } from './user-attributes-mapper';

function genderMapper(
  gender?: v1.UserAttributeCatalog['gender'],
): Gender | undefined {
  if (!gender) return undefined;

  return gender === 'MALE' ? Gender.Enum.MALE : Gender.Enum.FEMALE;
}

function tobaccoMapper(
  tobacco?: v1.UserAttributeCatalog['tobaccoUser'],
): TobaccoUse | undefined {
  if (!tobacco && typeof tobacco !== 'boolean') return undefined;

  return tobacco ? TobaccoUse.Enum.YES : TobaccoUse.Enum.NO;
}

function dateOfBirthMapper(
  dateOfBirth?: v1.UserAttributeCatalog['dateOfBirth'],
): Date | undefined {
  if (!dateOfBirth) return undefined;

  return new Date(dateOfBirth);
}

function memberTypeMapper(
  dependantIndex: v1.UserAttributeCatalogKeyMember,
): 'CHILD' | 'SPOUSE' {
  return dependantIndex.includes('Child')
    ? MemberType.Enum.CHILD
    : MemberType.Enum.SPOUSE;
}
function parseDependant<T extends Spouse | Child>(
  userAttributes: v1.UserAttribute[],
  userAttributesObject: ReturnType<typeof userAttributesMapper>,
  dependantIndex: v1.UserAttributeCatalogKeyMember,
): T | undefined {
  const dependantAttributes = userAttributes.filter((userAttribute) =>
    userAttribute.name.includes(dependantIndex),
  );

  if (dependantAttributes.length === 0) {
    return undefined;
  }

  const dependantGender = genderMapper(
    userAttributesObject[`${dependantIndex}-gender`],
  );

  const dependantDob = dateOfBirthMapper(
    userAttributesObject[`${dependantIndex}-dateOfBirth`],
  );

  const dependantTobacco = tobaccoMapper(
    userAttributesObject[`${dependantIndex}-tobacco`],
  );

  const memberType = memberTypeMapper(dependantIndex);

  return {
    memberType,
    dateOfBirth: dependantDob,
    gender: dependantGender,
    tobaccoUse: dependantTobacco,
  } as unknown as T;
}

export function memberMapper(
  userAttributes: v1.UserAttribute[],
  userAttributesMap: ReturnType<typeof userAttributesMapper>,
): { applicant: Applicant; spouse?: Spouse; children: Child[] } {
  const dateOfBirth = dateOfBirthMapper(userAttributesMap.dateOfBirth);

  const applicant: Applicant = removeEmptyKeys({
    memberType: 'PRIMARY',
    firstName: userAttributesMap.firstName,
    lastName: userAttributesMap.lastName,
    gender: genderMapper(userAttributesMap.gender),
    tobaccoUse: tobaccoMapper(userAttributesMap.tobaccoUser),
    dateOfBirth, // TODO - fix this iso parsing issue
  });

  const spouse: Spouse | undefined = parseDependant(
    userAttributes,
    userAttributesMap,
    'Spouse',
  );

  const children: Child[] = [];

  [1, 2, 3, 4].every((childIndex) => {
    const parsedChild: Child | undefined = parseDependant(
      userAttributes,
      userAttributesMap,
      `Child_${childIndex}` as v1.UserAttributeCatalogKeyMember,
    );
    if (parsedChild === undefined) {
      return false;
    }

    children.push(parsedChild);

    return true;
  });

  return { spouse, applicant, children };
}
